export interface HomologationChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface HomologationChatRequest {
  message: string;
  chatHistory: HomologationChatMessage[];
  organizationId: string;
  agentId: string;
}

export interface HomologationChatResponse {
  response: string;
  updatedChatHistory: HomologationChatMessage[];
}

export interface Comment {
  id: string;
  userName: string;
  text: string;
  selectedText?: string;
  messageId?: string;
  timestamp: string;
}

export interface AddCommentRequest {
  organizationId: string;
  agentId: string;
  userName: string;
  text: string;
  selectedText?: string;
  messageId?: string;
}

export interface AgentInfo {
  id: string;
  name: string;
  description?: string;
  model?: string;
  prompts?: { role: string; content: string }[];
}

export interface PromptSuggestion {
  id: string;
  userName: string;
  promptIndex: number;
  suggestionType: 'text' | 'image';
  suggestedText?: string;
  originalImageUrl?: string;
  suggestedImageUrl?: string;
  imageSource?: 'upload' | 'link';
  status: string;
  timestamp: string;
}

export interface AddPromptSuggestionRequest {
  organizationId: string;
  agentId: string;
  userName: string;
  promptIndex: number;
  suggestionType: 'text' | 'image';
  suggestedText?: string;
  originalImageUrl?: string;
  suggestedImageUrl?: string;
  imageSource?: 'upload' | 'link';
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function normalizePromptSuggestion(raw: Record<string, unknown>): PromptSuggestion {
  return {
    id: String(raw.Id ?? raw.id ?? ''),
    userName: String(raw.UserName ?? raw.userName ?? ''),
    promptIndex: Number(raw.PromptIndex ?? raw.promptIndex ?? 0),
    suggestionType: ((raw.SuggestionType ?? raw.suggestionType) as string)?.toLowerCase() === 'image' ? 'image' : 'text',
    suggestedText: raw.SuggestedText != null ? String(raw.SuggestedText) : raw.suggestedText != null ? String(raw.suggestedText) : undefined,
    originalImageUrl: raw.OriginalImageUrl != null ? String(raw.OriginalImageUrl) : raw.originalImageUrl != null ? String(raw.originalImageUrl) : undefined,
    suggestedImageUrl: raw.SuggestedImageUrl != null ? String(raw.SuggestedImageUrl) : raw.suggestedImageUrl != null ? String(raw.suggestedImageUrl) : undefined,
    imageSource: (raw.ImageSource ?? raw.imageSource) as 'upload' | 'link' | undefined,
    status: String(raw.Status ?? raw.status ?? 'Pending'),
    timestamp: String(raw.Timestamp ?? raw.timestamp ?? ''),
  };
}

class HomologationChatApiService {
  async sendMessage(request: HomologationChatRequest): Promise<HomologationChatResponse> {
    const response = await fetch(`${API_BASE_URL}/HomologationChat/SendMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to send message');
    }

    return await response.json();
  }


  async addComment(request: AddCommentRequest): Promise<Comment> {
    const response = await fetch(`${API_BASE_URL}/HomologationChat/AddComment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to add comment');
    }

    return await response.json();
  }

  async getComments(organizationId: string, agentId: string): Promise<Comment[]> {
    const response = await fetch(`${API_BASE_URL}/HomologationChat/GetComments?organizationId=${organizationId}&agentId=${agentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to get comments');
    }

    return await response.json();
  }

  async clearChatHistory(organizationId: string, agentId: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/HomologationChat/ClearChatHistory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ organizationId, agentId }),
    });

    if (!response.ok) {
      throw new Error('Failed to clear chat history');
    }

    return await response.json();
  }

  async getAgentInfo(organizationId: string, agentId: string): Promise<AgentInfo> {
    const response = await fetch(`${API_BASE_URL}/HomologationChat/GetAgentInfo?organizationId=${organizationId}&agentId=${agentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to get agent info');
    }

    return await response.json();
  }

  async getPromptSuggestions(organizationId: string, agentId: string): Promise<PromptSuggestion[]> {
    const response = await fetch(`${API_BASE_URL}/HomologationChat/GetPromptSuggestions?organizationId=${organizationId}&agentId=${agentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to get prompt suggestions');
    }

    const data = await response.json();
    return Array.isArray(data) ? data.map((s: Record<string, unknown>) => normalizePromptSuggestion(s)) : [];
  }

  async addPromptSuggestion(request: AddPromptSuggestionRequest): Promise<PromptSuggestion> {
    const response = await fetch(`${API_BASE_URL}/HomologationChat/AddPromptSuggestion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to add prompt suggestion');
    }

    const data = await response.json();
    return normalizePromptSuggestion(data as Record<string, unknown>);
  }
}

export const homologationChatApi = new HomologationChatApiService();
